import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext, ThemeContext } from '../Context';
import { normalizeEditorHtmlSpaces } from '../../utils/normalizeRubricHtml';
import { getScrollbarCss } from '../../utils/scrollbar';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CardContent,
  IconButton,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';

// TinyMCE (self-hosted) imports
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import 'tinymce-i18n/langs/fr_FR';

import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/autoresize';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/code';
import 'tinymce/plugins/image';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/table';

import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/ui/oxide/content.min.css';
import { Editor } from '@tinymce/tinymce-react';

const buildTinymceConfig = (token) => ({
  menubar: false,
  language: 'fr_FR',
  plugins: [
    'lists', 'autoresize', 'wordcount', 'fullscreen', 'table', 'searchreplace', 'image'
  ],
  toolbar: 'undo redo | bold italic underline strikethrough | forecolor backcolor | bullist numlist blockquote | alignleft aligncenter alignright alignjustify | indent outdent | table | image | removeformat searchreplace | fullscreen',
  content_style: 'body { font-family: Helvetica, Arial, sans-serif; font-size: 15px; }',
  branding: false,
  elementpath: false,
  statusbar: false,
  paste_as_text: false,
  paste_webkit_styles: 'none',
  paste_remove_styles: false,
  paste_merge_formats: true,
  smart_paste: false,
  promotion: false,
  contextmenu: false,
  skin: false,
  content_css: false,
  // Fullscreen natif du navigateur pour éviter les problèmes de z-index
  fullscreen_native: true,
  min_height: 200,
  autoresize_min_height: 200,
  autoresize_max_height: 600,
  autoresize_bottom_margin: 16,
  images_upload_credentials: true,
  file_picker_types: 'image',
  image_description: false,
  images_upload_handler: async (blobInfo) => {
    const formData = new FormData();
    formData.append('image', blobInfo.blob(), blobInfo.filename());

    try {
      const response = await axios.post('/api/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        withCredentials: true
      });

      const url = response.data?.url;
      if (!url) {
        throw new Error("Réponse inattendue du serveur d'upload.");
      }

      return url;
    } catch (error) {
      const message = error.response?.data?.message || "Échec de l'upload de l'image.";
      throw new Error(message);
    }
  },
  paste_postprocess: (plugin, args) => {
    args.node.innerHTML = normalizeEditorHtmlSpaces(args.node.innerHTML);
  }
});

function RichTextEditor({ value, onChange, placeholder, isDark }) {
  const handleEditorChange = (content) => {
    const normalizedContent = normalizeEditorHtmlSpaces(content);
    onChange(normalizedContent);
  };

  const editorConfig = {
    ...buildTinymceConfig(sessionStorage.getItem('authToken')),
    placeholder: placeholder || "Écrivez ici le contenu de la rubrique...",
    content_style: `
      body {
        font-family: Helvetica, Arial, sans-serif;
        font-size: 15px;
        color: ${isDark ? '#ecf0f1' : 'inherit'};
        background-color: ${isDark ? '#22303e' : '#ffffff'};
      }
      p { margin: 0.5em 0; }
      ul, ol { margin: 0.5em 0; padding-left: 2em; }
      .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
        color: ${isDark ? '#95a5a6' : '#aaa'};
      }
      ${getScrollbarCss(isDark)}
    `
  };

  return (
    <Box sx={{
      '& .tox-tinymce': {
        borderRadius: '4px',
        borderColor: isDark ? '#22303e' : '#e0e0e0',
        minHeight: '120px'
      },
      '& .tox-toolbar': {
        backgroundColor: isDark ? '#21303c' : '#f5f5f5',
        borderColor: isDark ? '#22303e' : '#e0e0e0',
      },
      '& .tox-editor-container': {
        backgroundColor: isDark ? '#22303e' : '#ffffff',
        borderColor: isDark ? '#22303e' : '#e0e0e0',
      },
      mt: 1,
      mb: 2
    }}>
      <Editor
        value={normalizeEditorHtmlSpaces(value) || ''}
        init={editorConfig}
        onEditorChange={handleEditorChange}
      />
    </Box>
  );
}

const MyRubrics = () => {
  const { user } = useContext(UserContext);
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  const [rubricTitle, setRubricTitle] = useState('');
  const [rubricContent, setRubricContent] = useState('');
  const [userRubrics, setUserRubrics] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingRubric, setEditingRubric] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    const fetchRubrics = async () => {
      try {
        const token = sessionStorage.getItem('authToken');
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(`/api/user/${user}/rubrics`, config);
        setUserRubrics(response.data.myRubrics || []);
      } catch (err) {
        setError('Erreur lors de la récupération des rubriques');
        console.error(err.response ? err.response.data : err);
      }
    };

    if (user) fetchRubrics();
  }, [user]);

  const sanitizeInput = (input) => {
    return input
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/on\w+="[^"]*"/gi, '')
      .replace(/javascript:/gi, '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!rubricTitle.trim() || !rubricContent.trim()) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    try {
      const token = sessionStorage.getItem('authToken');
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const sanitizedTitle = sanitizeInput(rubricTitle);
      const sanitizedContent = sanitizeInput(rubricContent);

      if (editingRubric) {
        await axios.put(
          `/api/user/${user}/rubrics/${editingRubric.name}`,
          { name: sanitizedTitle, content: sanitizedContent },
          config
        );
        setSuccess('Rubrique mise à jour avec succès');
      } else {
        await axios.post(
          `/api/user/${user}/rubrics`,
          { name: sanitizedTitle, content: sanitizedContent },
          config
        );
        setSuccess('Rubrique enregistrée avec succès');
      }

      setRubricTitle('');
      setRubricContent('');
      setEditingRubric(null);

      const rubricsResponse = await axios.get(`/api/user/${user}/rubrics`, config);
      setUserRubrics(rubricsResponse.data.myRubrics || []);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'enregistrement de la rubrique");
      console.error(err.response ? err.response.data : err);
    }
  };

  const handleEdit = (rubric) => {
    setEditingRubric(rubric);
    setRubricTitle(rubric.name);
    setRubricContent(rubric.content);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;

    try {
      const token = sessionStorage.getItem('authToken');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(`/api/user/${user}/rubrics/${confirmDelete.name}`, config);
      setSuccess('Rubrique supprimée avec succès');

      const response = await axios.get(`/api/user/${user}/rubrics`, config);
      setUserRubrics(response.data.myRubrics || []);
      setConfirmDelete(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
      console.error(err.response ? err.response.data : err);
    }
  };

  if (!user) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        <Alert severity="warning">Veuillez vous connecter pour gérer vos rubriques.</Alert>
      </Box>
    );
  }

  return (
    <Box
      component={Paper}
      elevation={2}
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: isDark ? '#233044' : '#ffffff',
        color: isDark ? '#fff' : 'inherit',
        boxShadow: isDark
          ? '0 4px 20px rgba(0, 0, 0, 0.5)'
          : '0 4px 20px rgba(0,0,0,0.05)',
      }}
    >
      <CardContent>
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          sx={{
            mb: 3,
            color: isDark ? '#90caf9' : 'inherit'
          }}
        >
          Gérer mes rubriques
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ mb: 4 }}>
          <TextField
            fullWidth
            label="Titre de la rubrique"
            value={rubricTitle}
            onChange={(e) => setRubricTitle(e.target.value)}
            margin="normal"
            variant="outlined"
            required
            sx={{
              mb: 2,
              '& .MuiInputLabel-root': {
                color: isDark ? '#90caf9' : 'inherit'
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'inherit',
                color: isDark ? '#fff' : 'inherit',
                '& fieldset': {
                  borderColor: isDark ? 'rgba(144, 202, 249, 0.3)' : 'inherit'
                },
                '&:hover fieldset': {
                  borderColor: isDark ? '#90caf9' : 'inherit'
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDark ? '#90caf9' : 'inherit'
                }
              }
            }}
          />
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 1,
                color: isDark ? '#90caf9' : 'inherit'
              }}
            >
              Contenu de la rubrique <span style={{ color: 'red' }}>*</span>
            </Typography>
            <RichTextEditor
              value={rubricContent}
              onChange={(value) => setRubricContent(value)}
              placeholder="Écrivez ici le contenu de la rubrique..."
              isDark={isDark}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              sx={{
                flexGrow: 1,
                bgcolor: isDark ? '#90caf9' : 'inherit',
                color: isDark ? '#1a253c' : 'inherit',
                '&:hover': {
                  bgcolor: isDark ? '#64b5f6' : 'inherit'
                }
              }}
            >
              {editingRubric ? 'Mettre à jour' : 'Enregistrer'}
            </Button>
            {editingRubric && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setEditingRubric(null);
                  setRubricTitle('');
                  setRubricContent('');
                }}
                sx={{
                  flexGrow: 1,
                  borderColor: isDark ? '#64b5f6' : 'inherit',
                  color: isDark ? '#64b5f6' : 'inherit',
                  '&:hover': {
                    borderColor: isDark ? '#90caf9' : 'inherit',
                    backgroundColor: isDark ? 'rgba(100, 181, 246, 0.1)' : 'inherit'
                  }
                }}
              >
                Annuler
              </Button>
            )}
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: isDark
              ? '1px solid rgba(144, 202, 249, 0.3)'
              : '1px solid #e0e0e0',
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'inherit'
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: isDark
                    ? 'rgba(144, 202, 249, 0.1)'
                    : '#f5f5f5'
                }}
              >
                <TableCell
                  sx={{
                    color: isDark ? '#90caf9' : 'inherit',
                    fontWeight: 600
                  }}
                >
                  Titre de la rubrique
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    color: isDark ? '#90caf9' : 'inherit',
                    fontWeight: 600
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userRubrics
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((rubric) => (
                  <TableRow
                    key={rubric.name}
                    sx={{
                      '&:hover': {
                        backgroundColor: isDark
                          ? 'rgba(144, 202, 249, 0.05)'
                          : '#fafafa'
                      }
                    }}
                  >
                    <TableCell sx={{ color: isDark ? '#fff' : 'inherit' }}>
                      {rubric.name}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        onClick={() => handleEdit(rubric)}
                        size="small"
                        sx={{
                          mr: 1,
                          color: isDark ? '#90caf9' : 'inherit',
                          '&:hover': {
                            backgroundColor: isDark
                              ? 'rgba(144, 202, 249, 0.1)'
                              : 'inherit'
                          }
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => setConfirmDelete(rubric)}
                        size="small"
                        sx={{
                          color: isDark ? '#f48fb1' : 'inherit',
                          '&:hover': {
                            backgroundColor: isDark
                              ? 'rgba(244, 143, 177, 0.1)'
                              : 'inherit'
                          }
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            backgroundColor: isDark ? '#233044' : '#ffffff',
            color: isDark ? '#fff' : 'inherit',
            '& .MuiDialog-paper': {
              backgroundColor: isDark ? '#233044' : '#ffffff'
            }
          }
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: isDark ? '#233044' : '#ffffff',
            color: isDark ? '#90caf9' : 'inherit',
            borderBottom: isDark ? '1px solid rgba(144, 202, 249, 0.2)' : '1px solid #e0e0e0',
            mb: 0
          }}
        >
          Confirmer la suppression
        </DialogTitle>
        <DialogContent
          sx={{
            py: 3,
            px: 3,
            color: isDark ? '#fff' : 'inherit',
            backgroundColor: isDark ? '#233044' : '#ffffff'
          }}
        >
          Êtes-vous sûr de vouloir supprimer la rubrique "{confirmDelete?.name}" ?
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            backgroundColor: isDark ? '#233044' : '#ffffff',
            borderTop: isDark ? '1px solid rgba(144, 202, 249, 0.2)' : '1px solid #e0e0e0'
          }}
        >
          <Button
            onClick={() => setConfirmDelete(null)}
            sx={{ color: isDark ? '#64b5f6' : 'inherit' }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            sx={{
              bgcolor: isDark ? '#f48fb1' : 'inherit',
              color: isDark ? '#1a253c' : 'inherit',
              '&:hover': {
                bgcolor: isDark ? '#f06292' : 'inherit'
              }
            }}
          >
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyRubrics;
